import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BackButtonService } from '../../back-button.service';
import { CodaltComponent } from '../../codalt.component';
import { LocalStorage } from '../../local-storage.class';
import { ApiService } from '../../services/api/api.service';
import { ConfirmDialogService } from '../../services/confirm-dialog-service/confirm-dialog.service';
import { CustomerService } from '../../services/customer/customer.service';
import { PageTitleService } from '../../services/page-title/page-title.service';
import { Contact } from '../shared/contact';

@Component({
    selector: 'app-contact-edit',
    templateUrl: './contact-edit.component.html',
    styleUrls: ['./contact-edit.component.scss'],
    standalone: false,
})
export class ContactEditComponent extends CodaltComponent implements OnInit, OnDestroy {
    contact: Contact = <Contact>{};
    contactId: number;
    customerId: number;
    projectId: number;
    imagePreview: string = null;

    constructor(
        private customerService: CustomerService,
        private router: Router,
        private backButtonService: BackButtonService,
        private apiService: ApiService,
        private ngZone: NgZone,
        private confirmDialogService: ConfirmDialogService,
        private pageTitleService: PageTitleService
    ) {
        super();
    }

    ngOnInit() {
        this.pageTitleService.setTitle('Contactpersoon bewerken');
        this.watchRouteParams(
            [this.RouteParamnames.customerId, this.RouteParamnames.projectId, this.RouteParamnames.contactId],
            ({ customerId, projectId, contactId }) => {
                this.customerId = +customerId;
                this.projectId = +projectId;
                if (contactId !== this.Routenames.newItem) {
                    this.contactId = +contactId;
                    this.subscriptions.add(
                        this.customerService.contact(this.customerId, this.contactId).subscribe(contact => {
                            this.contact = contact;
                            this.pageTitleService.setTitle('Contactpersoon bewerken', this.contact.name);
                        })
                    );
                } else {
                    this.contactId = null;
                    this.contact = <Contact>{};
                    this.contact.customer_project_id = this.projectId;
                    this.contact.customer_id = this.customerId;
                }
            }
        );
    }

    ngOnDestroy() {
        this.clearPreview();
        super.ngOnDestroy();
    }

    variableIsPresent(variable: any) {
        if (typeof variable === 'undefined') {
            return false;
        }

        return variable.length > 0;
    }

    save(form: FormGroup) {
        if (!form.valid) {
            return;
        }
        this.subscriptions.add(
            this.customerService.storeContact(this.customerId, this.contact).subscribe(
                () => this.backButtonService.navigateBack(),
                error => this.confirmDialogService.confirm('Fout tijdens opslaan contactpersoon', error, 'Oke')
            )
        );
    }

    delete() {
        this.confirmDialogService
            .confirm(
                'Contactpersoon verwijderen',
                'Weet je zeker dat je deze contactpersoon wilt verwijderen?',
                'Ja',
                'Nee'
            )
            .then(() =>
                this.subscriptions.add(
                    this.customerService
                        .deleteContact(LocalStorage.getCustomer()['id'], this.contact.id)
                        .subscribe(() => this.router.navigate([this.Routenames.contacts]))
                )
            )
            .catch(() =>
                this.confirmDialogService.confirm(
                    'Fout bij verwijderen contactpersoon',
                    'Kon contactpersoon niet verwijderen.',
                    'Oke'
                )
            );
    }

    uploadLogo(event) {
        event.stopPropagation();
        event.preventDefault();
        const file = event.srcElement.files[0];
        if (file.type === 'image/svg+xml') {
            this.uploadImage(file);
        } else if (['image/jpeg', 'image/png'].indexOf(file.type) !== -1) {
            const img = new Image();
            img.src = window.URL.createObjectURL(file);
            img.onload = () => {
                // Scale down so the longest side is at most Full HD, smaller images are left as they are
                const factor = 1920 / Math.max(img.naturalWidth, img.naturalHeight);
                const width = factor < 1 ? img.naturalWidth * factor : img.naturalWidth;
                const height = factor < 1 ? img.naturalHeight * factor : img.naturalHeight;
                const elem = document.createElement('canvas');
                elem.width = width;
                elem.height = height;
                elem.getContext('2d').drawImage(img, 0, 0, width, height);
                window.URL.revokeObjectURL(img.src);
                const extension = file.type === 'image/png' ? 'png' : 'jpg';
                elem.toBlob(
                    blob => {
                        // zone.js does not patch toBlob, so its callback runs outside Angular and would
                        // leave the view untouched until some other event triggers change detection
                        this.ngZone.run(() => {
                            this.uploadImage(new File([blob], `photo.${extension}`, { type: file.type }));
                        });
                    },
                    file.type,
                    0.85
                );
            };
        } else {
            this.confirmDialogService
                .confirm('Verkeerd bestand', 'Dit bestandsformaat wordt niet ondersteund', 'Oké')
                .then(() => {})
                .catch(() => {});
        }
    }

    private uploadImage(file: File) {
        // Show the chosen photo straight away, so we don't wait for the upload and the download that follows it
        this.clearPreview();
        this.imagePreview = window.URL.createObjectURL(file);
        this.subscriptions.add(
            this.apiService
                .makeFileRequest$<{ kind: string; file: string }>('file/upload', file, { storage: 'contacts' })
                .subscribe(
                    response => {
                        if (response.success) {
                            // Replace the reference so the contact-photo component picks up the new photo
                            this.contact = Object.assign({}, this.contact, { image_file: response.data.file });
                        } else {
                            this.clearPreview();
                            this.confirmDialogService.confirm('Fout tijdens uploaden foto', response.message, 'Oké');
                        }
                    },
                    error => {
                        this.clearPreview();
                        this.confirmDialogService.confirm(
                            'Fout tijdens uploaden foto',
                            error['errormessage'] ? error['errormessage'] : 'De foto kon niet worden geüpload',
                            'Oké'
                        );
                    }
                )
        );
    }

    private clearPreview() {
        if (this.imagePreview) {
            window.URL.revokeObjectURL(this.imagePreview);
            this.imagePreview = null;
        }
    }
}
